import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Minus, Sparkles } from "lucide-react";
import { getPriceId } from "@/utils/stripeEnvironment";

/**
 * PricingTable
 *
 * O que faz:
 * - Exibe três planos (Basic, Pro e Premium) lado a lado, com lista de recursos e CTA.
 * - Integra com Stripe passando o Price ID correto para cada plano
 *
 * Onde é usado:
 * - Na LandingPage, seção "Planos".
 *
 * Como se conecta:
 * - Componente visual isolado. O CTA navega para /signup com o parâmetro do plano
 * - Usa getPriceId() de stripeEnvironment.ts para obter Price IDs corretos
 */
export interface PricingPlan {
  name: string;
  price: string;
  description: string;
  features: { label: string; included: boolean }[];
  highlight?: boolean;
}

export interface PricingTableProps {
  onSelect?: (plan: string) => void;
}

// A copy acompanha usePlanPermissions: follow-up no Premium e links personalizados a partir do Pro.
const plans: PricingPlan[] = [
  {
    name: "Basic",
    price: "R$ 67/mês",
    description: "Ideal para iniciar com organização e automação básica",
    features: [
      { label: "WhatsApp integrado", included: true },
      { label: "Rastreamento de anúncios", included: true },
      { label: "Links personalizados", included: false },
      { label: "Funil visual (Kanban)", included: true },
      { label: "Agenda de consultas", included: true },
      { label: "IA básica de atendimento", included: true },
      { label: "2 relatórios de IA/mês", included: true },
      { label: "Follow-up automático", included: false },
      { label: "Import/Export contatos", included: false },
      { label: "Disparo programado", included: false },
      { label: "Instagram", included: false },
      { label: "Prontuário médico", included: false },
      { label: "IA de agendamento", included: false },
    ],
  },
  {
    name: "Pro",
    price: "R$ 147/mês",
    description: "Para ampliar o atendimento e acompanhar os resultados",
    features: [
      { label: "Tudo do plano Basic", included: true },
      { label: "Links personalizados", included: true },
      { label: "IA completa de atendimento", included: true },
      { label: "4 Relatórios IA/mês", included: true },
      { label: "Import/Export contatos", included: true },
      { label: "Disparo programado de mensagens", included: true },
      { label: "Chat do Instagram integrado", included: true },
      { label: "Follow-up automático", included: false },
      { label: "Prontuário médico", included: false },
      { label: "IA de agendamento automático", included: false },
      { label: "Suporte prioritário", included: false },
    ],
    highlight: true,
  },
  {
    name: "Premium",
    price: "R$ 247/mês",
    description: "Para automatizar retornos e reunir a rotina da clínica",
    features: [
      { label: "Tudo dos planos anteriores", included: true },
      { label: "Prontuário médico completo", included: true },
      { label: "IA de agendamento automático", included: true },
      { label: "Relatórios ilimitados", included: true },
      { label: "Suporte prioritário dedicado", included: true },
      { label: "Follow-up automático", included: true },
    ],
  },
];

export const PricingTable: React.FC<PricingTableProps> = ({ onSelect }) => {
  const handleSelectPlan = (planName: string) => {
    if (onSelect) {
      onSelect(planName);
    } else {
      // Mapear nome do plano para tier do Stripe
      const tierMap: { [key: string]: 'basic' | 'pro' | 'premium' } = {
        'Basic': 'basic',
        'Pro': 'pro',
        'Premium': 'premium'
      };
      
      const tier = tierMap[planName];
      const priceId = getPriceId(tier);
      
      // Redirecionar para signup com parâmetros do plano
      window.location.href = `/signup?plan=${tier}&priceId=${priceId}`;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
      {plans.map((p) => (
        <Card 
          key={p.name} 
          className={`relative ${p.highlight ? "border-2 shadow-xl scale-105 z-10" : "border"}`} 
          style={p.highlight ? { borderColor: "hsl(var(--brand))" } : undefined}
        >
          {p.highlight && (
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-yellow-400 text-yellow-900 font-bold px-4 py-1 text-sm">
                <Sparkles className="inline mr-1" size={14} />
                MAIS RECURSOS
              </Badge>
            </div>
          )}
          
          <CardHeader>
            <div className="text-center">
              <CardTitle className="text-2xl mb-2">{p.name}</CardTitle>
              <div className="text-3xl font-bold mb-2" style={{ color: "hsl(var(--brand))" }}>
                {p.price}
              </div>
              <p className="text-sm text-muted-foreground h-12">{p.description}</p>
            </div>
          </CardHeader>
          
          <CardContent>
            <ul className="space-y-3 mb-6">
              {p.features.map((f) => (
                <li key={f.label} className="flex items-start gap-2 text-sm">
                  {f.included ? (
                    <Check className="size-5 flex-shrink-0 mt-0.5" style={{ color: "hsl(var(--brand))" }} aria-hidden />
                  ) : (
                    <Minus className="size-5 flex-shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
                  )}
                  <span className={f.included ? "" : "text-muted-foreground"}>{f.label}</span>
                </li>
              ))}
            </ul>
            
            <div className="mt-6">
              <Button
                variant={p.highlight ? "default" : "outline"}
                className={`w-full ${p.highlight ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg" : ""}`}
                aria-label={`Criar conta e escolher o plano ${p.name}`}
                onClick={() => handleSelectPlan(p.name)}
              >
                Escolher {p.name}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
